import React, { createRef, PureComponent } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import {
  MAX_ATTACHED_IMAGES,
  MAX_ATTACHED_FILES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_LABEL,
  ALLOWED_FILE_EXTENSIONS,
  ALLOWED_FILE_ACCEPT
} from "../shared/mcp-chat-config";
import { EFFORT_DESCRIPTIONS, EFFORT_LABELS, EFFORT_ORDER } from "../shared/mcp-chat-effort";
import type {
  AttachedFile,
  AttachedImage,
  ChatMode,
  EffortTier,
  MentionReference,
  MentionSuggestion,
  SelectedElementAttachment,
  SlashCommandDefinition
} from "../shared/mcp-chat-types";
import McpChatCommandPalette from "./mcp-chat-command-palette";
import ContentEditableInput from "./mcp-chat-content-editable";
import { gatherMentionSuggestions } from "./mcp-chat-mention-data";
import McpChatMentionDropdown from "./mcp-chat-mention-dropdown";
import { filterCommands, findCommand, parseSlashInput } from "./mcp-chat-slash-commands";
import { VoiceRecorder, type RecorderState } from "./mcp-chat-voice-recorder";

export interface McpChatInputAreaProps {
  inputValue: string;
  isLoading: boolean;
  attachedImages: AttachedImage[];
  selectedElementAttachments: SelectedElementAttachment[];
  chatMode: ChatMode;
  isInspectorActive: boolean;
  isInspectorAvailable: boolean;
  onSend: () => void;
  onStop: () => void;
  onInputChange: (value: string) => void;
  attachedFiles: AttachedFile[];
  onAttachImages: (images: AttachedImage[]) => void;
  onRemoveImage: (imageId: string) => void;
  onAttachFiles: (files: AttachedFile[]) => void;
  onRemoveFile: (fileId: string) => void;
  onSlashCommandSelect: (cmd: SlashCommandDefinition, args: string) => void;
  onSendPrompt: (prompt: string) => void;
  onChatModeChange: (mode: ChatMode) => void;
  /** Current effort tier (Lite/Economy/Power). */
  effort: EffortTier;
  /** Change the effort tier. Absent → selector hidden (non-OpenRouter provider). */
  onModelTierChange?: (effort: EffortTier) => void;
  planMode: boolean;
  onPlanModeToggle: () => void;
  onToggleInspector: () => void;
  onRemoveSelectedElement: (kuikaId: string) => void;
  isImprovingPrompt?: boolean;
  onImprovePrompt?: () => void;
  onCancelImprovePrompt?: () => void;
  mentions: MentionReference[];
  mentionCounter: number;
  onMentionsChange: (mentions: MentionReference[], counter: number) => void;
  onHistoryUp?: () => boolean;
  onHistoryDown?: () => boolean;
  /** Attach the currently-selected canvas element (Tab). Returns true if one was added. */
  onAddCurrentSelection?: () => boolean;
  /** Remaining budget in USD. null = unlimited, undefined = not fetched. */
  remainingBalance?: number | null;
  /** Whether the balance panel popover is open. */
  isBalancePanelOpen?: boolean;
  /** Toggle the balance panel popover. */
  onBalancePanelToggle?: () => void;
  /**
   * Start the project-analysis (discovery) walk. Undefined = the server does not
   * offer it, and the control is not rendered at all.
   *
   * Sits in the RIGHT-hand group with effort and credits rather than beside the
   * mode and Plan controls on the left: those configure the next message and are
   * toggles you set and leave, while this spends money the moment it is
   * confirmed. It costs nothing to press — the server quotes the run first and
   * asks for approval with the real numbers.
   */
  onProjectAnalysis?: () => void;
}

interface McpChatInputAreaState {
  showPalette: boolean;
  paletteCommands: SlashCommandDefinition[];
  selectedCommandIndex: number;
  recorderState: RecorderState;
  elapsedSeconds: number;
  audioLevel: number;
  errorMessage: string;
  showModeDropdown: boolean;
  showEffortDropdown: boolean;
  isDragOver: boolean;
  showAttachDropdown: boolean;
  showMentionDropdown: boolean;
  mentionSuggestions: MentionSuggestion[];
  mentionSelectedIndex: number;
  mentionDropdownPosition: { top: number; left: number } | null;
  mentionQuery: string;
  /** Tracks whether the input has non-empty content (for button enabled state).
   *  Decoupled from props.inputValue to avoid parent re-renders on every keystroke. */
  hasContent: boolean;
}

const CHAT_MODE_LABELS: Record<ChatMode, string> = {
  ask: "Ask",
  designer: "Designer",
  builder: "Builder"
};

const CHAT_MODE_DESCRIPTIONS: Record<ChatMode, string> = {
  designer: "UI design only — create, style, and arrange components",
  builder: "Full capabilities — UI design, backend, data binding, actions, and logic",
  ask: "Read-only — answers questions without making changes"
};

/** How many of the three ascending level bars are filled per tier. */
const EFFORT_FILL_COUNT: Record<EffortTier, number> = { lite: 1, economy: 2, power: 3 };

/**
 * Signal-strength glyph per effort tier — three ascending bars, N filled by
 * tier (Lite 1 → Economy 2 → Power 3) so the cheap→expensive / low→high
 * progression reads at a glance. Inherits `currentColor` (tinted per tier by
 * the surrounding class); unfilled bars stay faint to keep the "level" outline.
 */
function renderEffortGlyph(effort: EffortTier): React.ReactElement {
  const filled: number = EFFORT_FILL_COUNT[effort];
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="2" y="9" width="2.6" height="4.8" rx="1" fill="currentColor" opacity={filled >= 1 ? 1 : 0.28} />
      <rect x="6.7" y="5.8" width="2.6" height="8" rx="1" fill="currentColor" opacity={filled >= 2 ? 1 : 0.28} />
      <rect x="11.4" y="2.4" width="2.6" height="11.4" rx="1" fill="currentColor" opacity={filled >= 3 ? 1 : 0.28} />
    </svg>
  );
}

/** Interval between waveform samples (ms). ~80ms ≈ 12.5 samples/sec. */
const WAVEFORM_SAMPLE_INTERVAL_MS = 80;
/** Max bars visible in the waveform container. Older bars are trimmed. */
const WAVEFORM_MAX_BARS = 60;

function getModifierKey(): string {
  return navigator.platform?.toLowerCase().startsWith("mac") ? "Cmd" : "Ctrl";
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

class McpChatInputArea extends PureComponent<McpChatInputAreaProps, McpChatInputAreaState> {
  contentEditableRef = createRef<ContentEditableInput>();
  fileInputRef = createRef<HTMLInputElement>();
  documentFileInputRef = createRef<HTMLInputElement>();
  private attachDropdownRef = createRef<HTMLDivElement>();
  private modeDropdownRef = createRef<HTMLDivElement>();
  private effortDropdownRef = createRef<HTMLDivElement>();
  private isMounted_ = false;
  private voiceRecorder: VoiceRecorder | null = null;
  private errorDismissTimer: ReturnType<typeof setTimeout> | null = null;
  private dragEnterCounter = 0;
  /** Accumulated audio-level samples for the WhatsApp-style waveform. Mutated in-place (not in state) for perf — reads piggyback on audioLevel renders. */
  private waveformSamples: number[] = [];
  private lastWaveformSampleTime = 0;
  /** Text from voice transcription that needs to be synced into the contentEditable DOM. */
  private pendingTranscription: string | null = null;

  state: McpChatInputAreaState = {
    showPalette: false,
    paletteCommands: [],
    selectedCommandIndex: 0,
    recorderState: "idle",
    elapsedSeconds: 0,
    audioLevel: 0,
    errorMessage: "",
    showModeDropdown: false,
    showEffortDropdown: false,
    isDragOver: false,
    showAttachDropdown: false,
    hasContent: false,
    showMentionDropdown: false,
    mentionSuggestions: [],
    mentionSelectedIndex: 0,
    mentionDropdownPosition: null,
    mentionQuery: ""
  };

  componentDidMount() {
    this.isMounted_ = true;
    document.addEventListener("mousedown", this.handleOutsideClick);
    this.voiceRecorder = new VoiceRecorder({
      onStateChange: (recorderState) => {
        if (this.isMounted_) this.setState({ recorderState });
      },
      onTimeUpdate: (elapsedSeconds) => {
        if (this.isMounted_) this.setState({ elapsedSeconds });
      },
      onAudioLevel: (audioLevel) => {
        if (!this.isMounted_) return;
        this.setState({ audioLevel });
        // Accumulate waveform samples at a fixed interval for WhatsApp-style visualization
        const now = performance.now();
        if (now - this.lastWaveformSampleTime >= WAVEFORM_SAMPLE_INTERVAL_MS) {
          this.lastWaveformSampleTime = now;
          this.waveformSamples.push(audioLevel);
        }
      },
      onTranscription: (text) => {
        if (!this.isMounted_) return;
        // The contentEditable is readOnly while recording/processing, so
        // document.execCommand("insertText") silently fails. Instead, update
        // the input value through React state and sync the DOM once the
        // element becomes editable again after the recorder transitions to idle.
        const current = this.contentEditableRef.current?.getPlainText() ?? this.props.inputValue;
        const separator = current.length > 0 && !current.endsWith(" ") ? " " : "";
        const newValue = current + separator + text;
        this.props.onInputChange(newValue);
        this.setState({ hasContent: newValue.trim().length > 0 });
        this.pendingTranscription = newValue;
      },
      onError: (errorMessage) => {
        if (!this.isMounted_) return;
        this.setState({ errorMessage });
        this.scheduleErrorDismiss();
      }
    });
  }

  componentDidUpdate(prevProps: McpChatInputAreaProps) {
    // If inputValue was cleared externally (e.g., after send), clear the contentEditable
    if (prevProps.inputValue !== "" && this.props.inputValue === "") {
      this.contentEditableRef.current?.clear();
      this.closeMentionDropdown();
    }

    // Sync pending voice transcription into the contentEditable once it becomes editable.
    // The transcription callback fires while recorderState is still "processing" (readOnly),
    // so we defer DOM insertion until the recorder transitions to idle.
    if (this.pendingTranscription != null && this.state.recorderState === "idle") {
      const text = this.pendingTranscription;
      this.pendingTranscription = null;
      const ce = this.contentEditableRef.current;
      if (ce) {
        ce.clear();
        ce.insertTextAtCursor(text);
      }
    }
  }

  componentWillUnmount() {
    this.isMounted_ = false;
    document.removeEventListener("mousedown", this.handleOutsideClick);
    this.voiceRecorder?.destroy();
    this.voiceRecorder = null;
    if (this.errorDismissTimer) {
      clearTimeout(this.errorDismissTimer);
    }
  }

  /** Expose focus for parent to call */
  focus = () => {
    this.contentEditableRef.current?.focus();
  };

  resetHeight = () => {
    this.contentEditableRef.current?.resetHeight();
  };

  clearContent = () => {
    this.contentEditableRef.current?.clear();
    if (this.state.hasContent) {
      this.setState({ hasContent: false });
    }
  };

  setContent = (text: string) => {
    this.contentEditableRef.current?.setText(text);
    const newHasContent = text.trim().length > 0;
    if (this.state.hasContent !== newHasContent) {
      this.setState({ hasContent: newHasContent });
    }
  };

  /** Expose ref target so parent can check activeElement */
  getTextareaElement = (): HTMLElement | null => this.contentEditableRef.current?.getElement() ?? null;

  /** Check if the command palette or mention dropdown is currently open */
  isPaletteOpen = (): boolean => this.state.showPalette || this.state.showMentionDropdown;

  /** Check if a pill dropdown is currently open */
  isPillDropdownOpen = (): boolean => false;

  // ── Chat mode dropdown ──

  handleModeDropdownToggle = () => {
    this.setState((prev) => ({ showModeDropdown: !prev.showModeDropdown }));
  };

  handleModeSelect = (mode: ChatMode) => {
    this.setState({ showModeDropdown: false });
    this.props.onChatModeChange(mode);
  };

  // ── Effort (model tier) dropdown ──

  handleEffortDropdownToggle = () => {
    this.setState((prev) => ({ showEffortDropdown: !prev.showEffortDropdown }));
  };

  handleEffortSelect = (effort: EffortTier) => {
    this.setState({ showEffortDropdown: false });
    this.props.onModelTierChange?.(effort);
  };

  handleOutsideClick = (e: MouseEvent) => {
    if (
      this.state.showModeDropdown &&
      this.modeDropdownRef.current &&
      !this.modeDropdownRef.current.contains(e.target as Node)
    ) {
      this.setState({ showModeDropdown: false });
    }
    if (
      this.state.showEffortDropdown &&
      this.effortDropdownRef.current &&
      !this.effortDropdownRef.current.contains(e.target as Node)
    ) {
      this.setState({ showEffortDropdown: false });
    }
    if (
      this.state.showAttachDropdown &&
      this.attachDropdownRef.current &&
      !this.attachDropdownRef.current.contains(e.target as Node)
    ) {
      this.setState({ showAttachDropdown: false });
    }
    // Close mention dropdown on outside click
    if (this.state.showMentionDropdown) {
      const target = e.target as Node;
      const dropdown = document.querySelector(".mcp-mention-dropdown");
      if (dropdown && !dropdown.contains(target)) {
        this.closeMentionDropdown();
      }
    }
  };

  handleAttachClick = () => {
    this.fileInputRef.current?.click();
  };

  handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    this.processImageFiles(Array.from(files));
    e.target.value = "";
  };

  handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith("image/")) {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(file);
      }
    }

    if (imageFiles.length > 0) {
      e.preventDefault();
      this.processImageFiles(imageFiles);
    }
  };

  processImageFiles = (files: File[]) => {
    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE_BYTES).map((f) => f.name);
    const sized = files.filter((f) => f.size <= MAX_FILE_SIZE_BYTES);
    if (oversized.length > 0) this.setError(this.buildOversizedMessage(oversized));

    const remaining = MAX_ATTACHED_IMAGES - this.props.attachedImages.length;
    const toProcess = sized.slice(0, remaining);
    if (toProcess.length === 0) return;
    const pending: AttachedImage[] = [];
    let loaded = 0;

    toProcess.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (!this.isMounted_) return;
        const dataUrl = reader.result as string;
        const base64 = dataUrl.split(",")[1];
        const mediaType = file.type || "image/png";

        pending.push({
          id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          file,
          base64,
          mediaType,
          previewUrl: dataUrl
        });

        loaded++;
        if (loaded === toProcess.length) {
          this.props.onAttachImages(pending);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // ── Attach dropdown ──

  handleAttachDropdownToggle = () => {
    this.setState((prev) => ({ showAttachDropdown: !prev.showAttachDropdown }));
  };

  handleAttachImageClick = () => {
    this.setState({ showAttachDropdown: false });
    this.fileInputRef.current?.click();
  };

  handleAttachFileClick = () => {
    this.setState({ showAttachDropdown: false });
    this.documentFileInputRef.current?.click();
  };

  handleDocumentFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    this.processDocumentFiles(Array.from(files));
    e.target.value = "";
  };

  processDocumentFiles = (files: File[]) => {
    const { attachedFiles, onAttachFiles } = this.props;
    const remaining = MAX_ATTACHED_FILES - attachedFiles.length;
    if (remaining <= 0) return;

    const validFiles: File[] = [];
    const oversized: string[] = [];
    for (const file of files) {
      if (validFiles.length >= remaining) break;
      const ext = file.name.split(".").pop()?.toLowerCase() || "";
      if (!ALLOWED_FILE_EXTENSIONS.includes(ext)) continue;
      if (file.size > MAX_FILE_SIZE_BYTES) {
        oversized.push(file.name);
        continue;
      }
      validFiles.push(file);
    }
    if (oversized.length > 0) this.setError(this.buildOversizedMessage(oversized));
    if (validFiles.length === 0) return;

    const pending: AttachedFile[] = [];
    let loaded = 0;

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (!this.isMounted_) return;
        const dataUrl = reader.result as string;
        const base64 = dataUrl.split(",")[1];
        const ext = file.name.split(".").pop()?.toLowerCase() || "";
        pending.push({
          id: `file_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          file,
          base64,
          fileName: file.name,
          mediaType: file.type || "application/octet-stream",
          fileSize: file.size,
          fileExtension: ext
        });
        loaded++;
        if (loaded === validFiles.length) {
          onAttachFiles(pending);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // ── Drag & drop ──

  private hasImageFiles(dt: DataTransfer): boolean {
    for (let i = 0; i < dt.items.length; i++) {
      if (dt.items[i].kind === "file" && dt.items[i].type.startsWith("image/")) return true;
    }
    return false;
  }

  private hasDocumentFiles(dt: DataTransfer): boolean {
    for (let i = 0; i < dt.items.length; i++) {
      if (dt.items[i].kind === "file" && !dt.items[i].type.startsWith("image/")) {
        const ext = dt.items[i].type ? "" : ""; // can't reliably get ext from DataTransferItem
        return true;
      }
    }
    return false;
  }

  handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.dragEnterCounter++;
    if (e.dataTransfer) {
      // Show overlay for both image and document files
      this.setState({ isDragOver: true });
    }
  };

  handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.dragEnterCounter--;
    if (this.dragEnterCounter <= 0) {
      this.dragEnterCounter = 0;
      this.setState({ isDragOver: false });
    }
  };

  handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.dragEnterCounter = 0;
    this.setState({ isDragOver: false });

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const allFiles = Array.from(files);
    const imageFiles = allFiles.filter((f) => f.type.startsWith("image/"));
    const docFiles = allFiles.filter((f) => {
      if (f.type.startsWith("image/")) return false;
      const ext = f.name.split(".").pop()?.toLowerCase() || "";
      return ALLOWED_FILE_EXTENSIONS.includes(ext);
    });
    if (imageFiles.length > 0) this.processImageFiles(imageFiles);
    if (docFiles.length > 0) this.processDocumentFiles(docFiles);
  };

  // ── Voice recording ──

  handleMicClick = () => {
    this.waveformSamples = [];
    this.lastWaveformSampleTime = 0;
    this.voiceRecorder?.startRecording();
  };

  handleRecordingStop = () => {
    this.voiceRecorder?.stopRecording();
  };

  handleRecordingCancel = () => {
    this.voiceRecorder?.cancelRecording();
  };

  scheduleErrorDismiss = () => {
    if (this.errorDismissTimer) clearTimeout(this.errorDismissTimer);
    this.errorDismissTimer = setTimeout(() => {
      if (this.isMounted_) this.setState({ errorMessage: "" });
    }, 4000);
  };

  dismissError = () => {
    if (this.errorDismissTimer) clearTimeout(this.errorDismissTimer);
    this.setState({ errorMessage: "" });
  };

  setError = (errorMessage: string) => {
    if (!this.isMounted_) return;
    this.setState({ errorMessage });
    this.scheduleErrorDismiss();
  };

  private buildOversizedMessage(names: string[]): string {
    if (names.length === 1) {
      return `"${names[0]}" is over the ${MAX_FILE_SIZE_LABEL} limit and wasn't attached.`;
    }
    return `${names.length} files were over the ${MAX_FILE_SIZE_LABEL} limit and weren't attached.`;
  }

  // ── Palette logic ──

  updatePalette = (value: string) => {
    if (!value.startsWith("/")) {
      if (this.state.showPalette) {
        this.setState({ showPalette: false, paletteCommands: [], selectedCommandIndex: 0 });
      }
      return;
    }

    const parsed = parseSlashInput(value);
    if (!parsed) return;

    // Don't show palette if user already typed a full command with args
    if (parsed.isComplete) {
      if (this.state.showPalette) {
        this.setState({ showPalette: false, paletteCommands: [], selectedCommandIndex: 0 });
      }
      return;
    }

    const filtered = filterCommands(parsed.commandName, this.props.isLoading);
    this.setState({
      showPalette: filtered.length > 0,
      paletteCommands: filtered,
      selectedCommandIndex: 0
    });
  };

  handleCommandSelect = (cmd: SlashCommandDefinition) => {
    this.setState({ showPalette: false, paletteCommands: [], selectedCommandIndex: 0 });

    if (cmd.mode === "immediate") {
      // Clear the contentEditable immediately so stale text doesn't linger
      this.contentEditableRef.current?.clear();
      this.props.onInputChange("");
      this.props.onSlashCommandSelect(cmd, "");
    } else {
      // Argument mode: set input to "/cmd " and let user type args
      const prefill = `/${cmd.name} `;
      this.props.onInputChange(prefill);
      this.contentEditableRef.current?.setText(prefill);
      this.contentEditableRef.current?.focus();
    }
  };

  handlePaletteHover = (index: number) => {
    this.setState({ selectedCommandIndex: index });
  };

  closePalette = () => {
    this.setState({ showPalette: false, paletteCommands: [], selectedCommandIndex: 0 });
  };

  // ── Key handling ──

  handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const {
      showPalette,
      paletteCommands,
      selectedCommandIndex,
      showMentionDropdown,
      mentionSuggestions,
      mentionSelectedIndex
    } = this.state;

    // Mention dropdown navigation (takes priority)
    if (showMentionDropdown && mentionSuggestions.length > 0) {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        this.setState({
          mentionSelectedIndex: (mentionSelectedIndex - 1 + mentionSuggestions.length) % mentionSuggestions.length
        });
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.setState({
          mentionSelectedIndex: (mentionSelectedIndex + 1) % mentionSuggestions.length
        });
        return;
      }
      if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
        e.preventDefault();
        this.handleMentionSelect(mentionSuggestions[mentionSelectedIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        this.closeMentionDropdown();
        return;
      }
    }

    // Palette navigation
    if (showPalette && paletteCommands.length > 0) {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        this.setState({
          selectedCommandIndex: (selectedCommandIndex - 1 + paletteCommands.length) % paletteCommands.length
        });
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.setState({
          selectedCommandIndex: (selectedCommandIndex + 1) % paletteCommands.length
        });
        return;
      }
      if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
        e.preventDefault();
        this.handleCommandSelect(paletteCommands[selectedCommandIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        this.closePalette();
        return;
      }
    }

    // Shift+Tab chat-mode cycling was removed (KOA-17717) — mode is changed only
    // via the mode dropdown; Shift+Tab now falls through to normal focus handling.

    // Tab (no dropdown open): attach the currently-selected canvas element, just
    // like picking it with the inspector. Only swallow Tab when something was
    // actually added — otherwise it falls through to normal focus navigation.
    if (e.key === "Tab" && !e.shiftKey && this.props.onAddCurrentSelection) {
      const added: boolean = this.props.onAddCurrentSelection();
      if (added) {
        e.preventDefault();
        return;
      }
    }

    // ArrowUp/Down: browse message history when input is empty
    if (e.key === "ArrowUp" && this.props.onHistoryUp) {
      if (this.props.onHistoryUp()) {
        e.preventDefault();
        return;
      }
    }
    if (e.key === "ArrowDown" && this.props.onHistoryDown) {
      if (this.props.onHistoryDown()) {
        e.preventDefault();
        return;
      }
    }

    // Normal Enter: check if input is a slash command
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();

      const currentText = this.contentEditableRef.current?.getPlainText() ?? this.props.inputValue;
      if (currentText.startsWith("/")) {
        const parsed = parseSlashInput(currentText);
        if (parsed && parsed.commandName) {
          // Exact command match (e.g. "/clear")
          const cmd = findCommand(parsed.commandName);
          if (cmd) {
            this.props.onSlashCommandSelect(cmd, parsed.args);
            return;
          }
          // Partial match: if palette has filtered results, select the highlighted one
          // (handles "/cl" → selects "clear" if palette is visible)
          if (showPalette && paletteCommands.length > 0) {
            this.handleCommandSelect(paletteCommands[selectedCommandIndex]);
            return;
          }
        }
      }

      if (!currentText.trim()) return;

      this.props.onSend();
    }
  };

  private handleContentEditableInput = (plainText: string, mentions: MentionReference[]) => {
    this.props.onInputChange(plainText);
    const newHasContent = plainText.trim().length > 0;
    if (this.state.hasContent !== newHasContent) {
      this.setState({ hasContent: newHasContent });
    }
    this.updatePalette(plainText);

    // Sync mentions from DOM (in case chips were deleted or changed via backspace/undo)
    const prevMentions = this.props.mentions;
    if (mentions.length !== prevMentions.length || mentions.some((m, i) => m.id !== prevMentions[i]?.id)) {
      this.props.onMentionsChange(mentions, this.props.mentionCounter);
    }

    // Detect @ trigger after input
    requestAnimationFrame(() => {
      if (this.isMounted_) this.detectMentionTrigger();
    });
  };

  handleSelect = () => {
    // Use requestAnimationFrame to let the selection settle
    requestAnimationFrame(() => {
      if (this.isMounted_) this.detectMentionTrigger();
    });
  };

  // ── @ Mention dropdown ──

  private detectMentionTrigger = () => {
    if (!this.isMounted_) return;

    const ce = this.contentEditableRef.current;
    if (!ce) return;

    const editorEl = ce.getElement();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !editorEl) {
      this.closeMentionDropdown();
      return;
    }

    const range = sel.getRangeAt(0);
    const textNode = range.startContainer;

    // Verify selection is inside the contentEditable element
    if (textNode.nodeType !== Node.TEXT_NODE || !editorEl.contains(textNode)) {
      this.closeMentionDropdown();
      return;
    }

    const text = textNode.textContent || "";
    const cursorPos = range.startOffset;
    const beforeCursor = text.substring(0, cursorPos);

    // Find @ that's not preceded by a word character
    const atMatch = beforeCursor.match(/(^|[^a-zA-Z0-9])@([a-zA-Z0-9_]*)$/);
    if (!atMatch) {
      this.closeMentionDropdown();
      return;
    }

    const query = atMatch[2];
    const suggestions = gatherMentionSuggestions(query);

    if (suggestions.length === 0) {
      this.closeMentionDropdown();
      return;
    }

    // Position dropdown relative to the input container
    const caretRect = ce.getCaretRect();
    const containerEl = ce.getElement()?.closest(".mcp-chat__input-container");
    if (!caretRect || !containerEl) {
      this.closeMentionDropdown();
      return;
    }

    const containerRect = containerEl.getBoundingClientRect();
    const bottom = containerRect.bottom - caretRect.top + 4;
    const left = caretRect.left - containerRect.left;

    this.setState({
      showMentionDropdown: true,
      mentionSuggestions: suggestions,
      mentionSelectedIndex: 0,
      mentionDropdownPosition: { top: bottom, left: Math.max(0, Math.min(left, containerRect.width - 240)) },
      mentionQuery: query
    });
  };

  private closeMentionDropdown = () => {
    if (this.state.showMentionDropdown) {
      this.setState({
        showMentionDropdown: false,
        mentionSuggestions: [],
        mentionSelectedIndex: 0,
        mentionDropdownPosition: null,
        mentionQuery: ""
      });
    }
  };

  private handleMentionSelect = (suggestion: MentionSuggestion) => {
    const ce = this.contentEditableRef.current;
    if (!ce) return;

    const nextCounter = this.props.mentionCounter + 1;
    const mention: MentionReference = {
      id: nextCounter,
      category: suggestion.category,
      entityId: suggestion.entityId,
      displayName: suggestion.displayName
    };

    ce.insertMention(mention);
    this.closeMentionDropdown();

    // Update parent with new mentions list
    const newMentions = [...this.props.mentions, mention];
    this.props.onMentionsChange(newMentions, nextCounter);
  };

  private handleMentionHover = (index: number) => {
    this.setState({ mentionSelectedIndex: index });
  };

  // ── Render helpers ──

  renderRecordingOverlay() {
    const { recorderState, elapsedSeconds, audioLevel } = this.state;
    const isProcessing = recorderState === "processing";

    return (
      <div className="mcp-chat__recording-overlay">
        <button
          className="mcp-chat__recording-cancel"
          onClick={this.handleRecordingCancel}
          title="Cancel recording"
          aria-label="Cancel recording"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <line x1="2" y1="2" x2="10" y2="10" />
            <line x1="10" y1="2" x2="2" y2="10" />
          </svg>
        </button>

        <div className="mcp-chat__recording-center">
          {isProcessing ? (
            <>
              <svg
                className="mcp-chat__recording-spinner"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="9" cy="9" r="7" opacity="0.25" />
                <path d="M9 2a7 7 0 0 1 7 7" strokeLinecap="round" />
              </svg>
              <span className="mcp-chat__recording-processing-text">Transcribing...</span>
            </>
          ) : (
            <>
              <div className="mcp-chat__recording-waveform">
                {(this.waveformSamples.length > WAVEFORM_MAX_BARS
                  ? this.waveformSamples.slice(-WAVEFORM_MAX_BARS)
                  : this.waveformSamples
                ).map((level, i) => (
                  <span
                    key={i}
                    className="mcp-chat__recording-waveform-bar"
                    style={{ height: `${Math.max(3, Math.round(level * 24))}px` }}
                  />
                ))}
              </div>
              <span className="mcp-chat__recording-time">{formatTime(elapsedSeconds)}</span>
            </>
          )}
        </div>

        <button
          className="mcp-chat__recording-stop"
          onClick={this.handleRecordingStop}
          disabled={isProcessing}
          title="Stop and transcribe"
          aria-label="Stop and transcribe"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="2,7 5.5,10.5 12,3.5" />
          </svg>
        </button>
      </div>
    );
  }

  render() {
    const {
      inputValue,
      isLoading,
      attachedImages,
      attachedFiles,
      selectedElementAttachments,
      chatMode,
      planMode,
      isInspectorActive,
      isInspectorAvailable,
      onSend,
      onStop,
      onRemoveImage,
      onRemoveFile,
      onToggleInspector,
      onRemoveSelectedElement,
      isImprovingPrompt,
      onImprovePrompt,
      onCancelImprovePrompt,
      effort,
      onModelTierChange
    } = this.props;
    const {
      showPalette,
      paletteCommands,
      selectedCommandIndex,
      recorderState,
      errorMessage,
      showModeDropdown,
      showEffortDropdown,
      showMentionDropdown,
      mentionSuggestions,
      mentionSelectedIndex,
      mentionDropdownPosition,
      isDragOver,
      showAttachDropdown
    } = this.state;
    const isRecording = recorderState === "recording" || recorderState === "processing";

    return (
      <div
        className="mcp-chat__input-area"
        onDragEnter={this.handleDragEnter}
        onDragOver={this.handleDragOver}
        onDragLeave={this.handleDragLeave}
        onDrop={this.handleDrop}
      >
        {showPalette && paletteCommands.length > 0 && (
          <McpChatCommandPalette
            commands={paletteCommands}
            selectedIndex={selectedCommandIndex}
            onSelect={this.handleCommandSelect}
            onHover={this.handlePaletteHover}
          />
        )}
        {showMentionDropdown && mentionSuggestions.length > 0 && (
          <McpChatMentionDropdown
            suggestions={mentionSuggestions}
            selectedIndex={mentionSelectedIndex}
            position={mentionDropdownPosition}
            onSelect={this.handleMentionSelect}
            onHover={this.handleMentionHover}
          />
        )}
        {isDragOver && (
          <div className="mcp-chat__drop-overlay">
            <div className="mcp-chat__drop-overlay-content">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
              <span>Drop files here</span>
            </div>
          </div>
        )}
        <div
          className={`mcp-chat__input-container${isRecording ? " mcp-chat__input-container--recording" : ""}${isDragOver ? " mcp-chat__input-container--drag-over" : ""}${isInspectorActive ? " mcp-chat__input-container--inspector" : ""}`}
        >
          <input
            ref={this.fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={this.handleFileSelected}
          />
          <input
            ref={this.documentFileInputRef}
            type="file"
            accept={ALLOWED_FILE_ACCEPT}
            multiple
            style={{ display: "none" }}
            onChange={this.handleDocumentFileSelected}
          />
          {attachedImages.length > 0 && (
            <div className="mcp-chat__image-previews">
              {attachedImages.map((img) => (
                <div key={img.id} className="mcp-chat__image-preview">
                  <img src={img.previewUrl} alt="preview" />
                  <button
                    className="mcp-chat__image-preview-remove"
                    onClick={() => onRemoveImage(img.id)}
                    aria-label="Remove image"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          {attachedFiles.length > 0 && (
            <div className="mcp-chat__file-chips">
              {attachedFiles.map((f) => {
                const sizeKb = Math.round(f.fileSize / 1024);
                const sizeLabel = sizeKb >= 1024 ? `${(sizeKb / 1024).toFixed(1)} MB` : `${sizeKb} KB`;
                return (
                  <div key={f.id} className="mcp-chat__file-chip">
                    <svg
                      className="mcp-chat__file-chip-icon"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span className="mcp-chat__file-chip-name" title={f.fileName}>
                      {f.fileName.length > 25 ? f.fileName.slice(0, 22) + "..." : f.fileName}
                    </span>
                    <span className="mcp-chat__file-chip-size">{sizeLabel}</span>
                    <button
                      className="mcp-chat__file-chip-remove"
                      onClick={() => onRemoveFile(f.id)}
                      aria-label={`Remove ${f.fileName}`}
                    >
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <line x1="1.5" y1="1.5" x2="6.5" y2="6.5" />
                        <line x1="6.5" y1="1.5" x2="1.5" y2="6.5" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {selectedElementAttachments.length > 0 && (
            <div className="mcp-chat__selected-element-row">
              {selectedElementAttachments.map((att) => (
                <div
                  key={att.kuikaId}
                  className="mcp-chat__selected-element-chip"
                  data-testid="mcpChatSelectedElementChip"
                  title={`${att.componentName} (${att.kuikaId})`}
                >
                  <svg
                    className="mcp-chat__selected-element-chip-icon"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <line x1="22" y1="12" x2="18" y2="12" />
                    <line x1="6" y1="12" x2="2" y2="12" />
                    <line x1="12" y1="6" x2="12" y2="2" />
                    <line x1="12" y1="22" x2="12" y2="18" />
                    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
                  </svg>
                  <span className="mcp-chat__selected-element-chip-label">{att.displayName}</span>
                  <button
                    className="mcp-chat__selected-element-chip-remove"
                    onClick={() => onRemoveSelectedElement(att.kuikaId)}
                    aria-label={`Remove ${att.displayName}`}
                  >
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <line x1="1.5" y1="1.5" x2="6.5" y2="6.5" />
                      <line x1="6.5" y1="1.5" x2="1.5" y2="6.5" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="mcp-chat__editable-wrapper">
            <ContentEditableInput
              ref={this.contentEditableRef}
              className={`${isRecording ? "mcp-chat__content-editable--recording" : ""}${isImprovingPrompt ? " mcp-chat__content-editable--improving" : ""}`}
              readOnly={isRecording || !!isImprovingPrompt}
              placeholder={
                isImprovingPrompt
                  ? "Improving..."
                  : isInspectorActive
                    ? "Click an element on canvas..."
                    : "Ask Kuika AI..."
              }
              onInput={this.handleContentEditableInput}
              onKeyDown={this.handleKeyDown}
              onPaste={this.handlePaste}
              onSelect={this.handleSelect}
            />
            <KuikaTooltip content={isImprovingPrompt ? "Cancel improving" : "Improve prompt"} position="top">
              <button
                className={`mcp-chat__improve-btn${isImprovingPrompt ? " mcp-chat__improve-btn--active" : ""}`}
                onClick={isImprovingPrompt ? onCancelImprovePrompt : onImprovePrompt}
                disabled={isLoading || (!isImprovingPrompt && !this.state.hasContent)}
                aria-label={isImprovingPrompt ? "Cancel improving" : "Improve prompt"}
              >
                <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M8 0l1.5 4.5L14 6l-4.5 1.5L8 12l-1.5-4.5L2 6l4.5-1.5L8 0z" />
                  <path d="M12.5 9l.75 2.25L15.5 12l-2.25.75L12.5 15l-.75-2.25L9.5 12l2.25-.75L12.5 9z" opacity="0.7" />
                </svg>
              </button>
            </KuikaTooltip>
          </div>
        </div>
        <div className="mcp-chat__toolbar-footer">
          <div className="mcp-chat__toolbar-separator" />
          {isRecording ? (
            this.renderRecordingOverlay()
          ) : (
            <div className="mcp-chat__toolbar">
              <div className="mcp-chat__toolbar-left">
                <KuikaTooltip content={isInspectorActive ? "Cancel inspector" : "Select element"} position="top">
                  <button
                    className={`mcp-chat__toolbar-btn${isInspectorActive ? " mcp-chat__toolbar-btn--active" : ""}`}
                    onClick={onToggleInspector}
                    disabled={isLoading || !isInspectorAvailable}
                    aria-label={isInspectorActive ? "Cancel element inspector" : "Select element from canvas"}
                    aria-pressed={isInspectorActive}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.3" />
                      <path
                        d="M8 2.5v2M8 11.5v2M2.5 8h2M11.5 8h2"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </KuikaTooltip>
                <KuikaTooltip content="Attach" position="top" open={showAttachDropdown ? false : undefined}>
                  <div className="mcp-chat__attach-dropdown" ref={this.attachDropdownRef}>
                    <button
                      className="mcp-chat__toolbar-btn"
                      onClick={this.handleAttachDropdownToggle}
                      disabled={isLoading}
                      aria-label="Attach file or image"
                      aria-expanded={showAttachDropdown}
                      aria-haspopup="menu"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                      </svg>
                    </button>
                    {showAttachDropdown && (
                      <div className="mcp-chat__attach-dropdown-menu" role="menu">
                        <button
                          className="mcp-chat__attach-dropdown-item"
                          onClick={this.handleAttachImageClick}
                          role="menuitem"
                        >
                          <KuikaSvg name="image" type="common" width={14} height={14} />
                          <span>Image</span>
                        </button>
                        <button
                          className="mcp-chat__attach-dropdown-item"
                          onClick={this.handleAttachFileClick}
                          role="menuitem"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                          <span>File</span>
                        </button>
                        <div className="mcp-chat__attach-dropdown-hint">Max {MAX_FILE_SIZE_LABEL} per file</div>
                      </div>
                    )}
                  </div>
                </KuikaTooltip>
                <KuikaTooltip
                  content={CHAT_MODE_DESCRIPTIONS[chatMode]}
                  position="top"
                  open={showModeDropdown ? false : undefined}
                >
                  <div className="mcp-chat__mode-dropdown" ref={this.modeDropdownRef}>
                    <button
                      className="mcp-chat__mode-dropdown-trigger"
                      onClick={this.handleModeDropdownToggle}
                      disabled={isLoading}
                      aria-label="Select chat mode"
                      aria-expanded={showModeDropdown}
                      aria-haspopup="listbox"
                      data-testid="mcpChatModeSelector"
                    >
                      <span className={`mcp-chat__mode-dot mcp-chat__mode-dot--${chatMode}`} />
                      <span data-testid="mcpChatModeLabel" className="mcp-chat__mode-dropdown-label">
                        {CHAT_MODE_LABELS[chatMode]}
                      </span>
                      <svg
                        className={`mcp-chat__mode-dropdown-chevron${showModeDropdown ? " mcp-chat__mode-dropdown-chevron--open" : ""}`}
                        width="10"
                        height="10"
                        viewBox="0 0 10 10"
                        fill="none"
                      >
                        <path
                          d="M2.5 4L5 6.5L7.5 4"
                          stroke="currentColor"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    {showModeDropdown && (
                      <div className="mcp-chat__mode-dropdown-menu" role="listbox">
                        {(["designer", "builder", "ask"] as ChatMode[]).map((mode) => {
                          return (
                            <button
                              key={mode}
                              data-testid={`mcpChatModeOption-${mode}`}
                              className={`mcp-chat__mode-dropdown-item${chatMode === mode ? " mcp-chat__mode-dropdown-item--active" : ""}`}
                              onClick={() => this.handleModeSelect(mode)}
                              role="option"
                              aria-selected={chatMode === mode}
                              title={CHAT_MODE_DESCRIPTIONS[mode]}
                            >
                              <span className={`mcp-chat__mode-dot mcp-chat__mode-dot--${mode}`} />
                              <span className="mcp-chat__mode-dropdown-item-content">
                                <span className="mcp-chat__mode-dropdown-item-label">
                                  {CHAT_MODE_LABELS[mode]}
                                  {mode === "builder" && <span className="mcp-chat__mode-beta-badge">Beta</span>}
                                  {mode === "ask" && <span className="mcp-chat__mode-free-badge">FREE</span>}
                                </span>
                                <span className="mcp-chat__mode-dropdown-item-desc">
                                  {CHAT_MODE_DESCRIPTIONS[mode]}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </KuikaTooltip>
                <KuikaTooltip
                  content={
                    chatMode === "ask"
                      ? "Plan mode is not available in Ask mode"
                      : "Plan mode — AI explores and creates a plan before executing"
                  }
                  position="top"
                >
                  <button
                    className={`mcp-chat__plan-toggle${planMode ? " mcp-chat__plan-toggle--active" : ""}`}
                    onClick={this.props.onPlanModeToggle}
                    disabled={chatMode === "ask"}
                    aria-pressed={planMode}
                    aria-label="Toggle plan mode"
                    data-testid="mcpChatPlanToggle"
                  >
                    <KuikaSvg name="checkup-list" type="common" width={14} height={14} />
                    Plan
                  </button>
                </KuikaTooltip>
              </div>

              <div className="mcp-chat__toolbar-right">
                {onModelTierChange && (
                  <KuikaTooltip
                    content={`Model effort: ${EFFORT_LABELS[effort]}`}
                    position="top"
                    open={showEffortDropdown ? false : undefined}
                  >
                    <div className="mcp-chat__effort-dropdown" ref={this.effortDropdownRef}>
                      <button
                        className="mcp-chat__effort-trigger"
                        onClick={this.handleEffortDropdownToggle}
                        disabled={isLoading}
                        aria-label="Select model effort"
                        aria-haspopup="listbox"
                        aria-expanded={showEffortDropdown}
                        data-testid="mcpChatEffortSelector"
                        type="button"
                      >
                        <span className={`mcp-chat__effort-trigger-icon mcp-chat__effort-trigger-icon--${effort}`}>
                          {renderEffortGlyph(effort)}
                        </span>
                        <span className="mcp-chat__effort-trigger-label" data-testid="mcpChatEffortLabel">
                          {EFFORT_LABELS[effort]}
                        </span>
                        <svg
                          className={`mcp-chat__effort-chevron${showEffortDropdown ? " mcp-chat__effort-chevron--open" : ""}`}
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M2.5 4L5 6.5L7.5 4"
                            stroke="currentColor"
                            strokeWidth="1.3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      {showEffortDropdown && (
                        <div className="mcp-chat__effort-menu" role="listbox" aria-label="Model effort">
                          <div className="mcp-chat__effort-menu-head">
                            <span className="mcp-chat__effort-menu-title">Model effort</span>
                            <span className="mcp-chat__effort-menu-sub">
                              Higher effort delivers better quality at a higher cost.
                            </span>
                          </div>
                          {EFFORT_ORDER.map((tier) => (
                            <button
                              key={tier}
                              type="button"
                              data-testid={`mcpChatEffortOption-${tier}`}
                              className={`mcp-chat__effort-card${tier === "power" ? " mcp-chat__effort-card--power" : ""}${
                                effort === tier ? " mcp-chat__effort-card--active" : ""
                              }`}
                              onClick={() => this.handleEffortSelect(tier)}
                              role="option"
                              aria-selected={effort === tier}
                            >
                              <span className={`mcp-chat__effort-card-icon mcp-chat__effort-card-icon--${tier}`}>
                                {renderEffortGlyph(tier)}
                              </span>
                              <span className="mcp-chat__effort-card-body">
                                <span className="mcp-chat__effort-card-head">
                                  <span className="mcp-chat__effort-card-name">{EFFORT_LABELS[tier]}</span>
                                  {tier === "power" && (
                                    <span className="mcp-chat__effort-badge" title="Highest cost tier">
                                      $$$
                                    </span>
                                  )}
                                </span>
                                <span className="mcp-chat__effort-card-desc">{EFFORT_DESCRIPTIONS[tier]}</span>
                              </span>
                              {effort === tier && (
                                <svg
                                  className="mcp-chat__effort-card-check"
                                  width="14"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                  aria-hidden="true"
                                >
                                  <path
                                    d="M2.5 7.5L5.5 10.5L11.5 3.5"
                                    stroke="currentColor"
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </KuikaTooltip>
                )}
                {this.props.remainingBalance !== undefined && this.props.onBalancePanelToggle && (
                  <KuikaTooltip
                    content={
                      this.props.remainingBalance === null
                        ? "Credits: unlimited"
                        : `Credits: ${Math.floor(this.props.remainingBalance).toLocaleString("en-US")}`
                    }
                    position="top"
                  >
                    <button
                      className={(() => {
                        const parts = ["mcp-chat__balance-pill"];
                        const bal = this.props.remainingBalance;
                        if (bal !== null && bal !== undefined) {
                          if (bal <= 0) parts.push("mcp-chat__balance-pill--critical");
                          else if (bal < 20) parts.push("mcp-chat__balance-pill--critical");
                          else if (bal < 100) parts.push("mcp-chat__balance-pill--low");
                          else parts.push("mcp-chat__balance-pill--ok");
                        }
                        if (this.props.isBalancePanelOpen) parts.push("mcp-chat__balance-pill--active");
                        return parts.join(" ");
                      })()}
                      onClick={this.props.onBalancePanelToggle}
                      type="button"
                      aria-label={
                        this.props.remainingBalance !== null
                          ? `Remaining credits: ${this.props.remainingBalance.toLocaleString("en-US")}`
                          : "Credits (unlimited)"
                      }
                      data-testid="mcpChatBalancePill"
                    >
                      <span className="mcp-chat__balance-pill-icon" aria-hidden="true">
                        <KuikaSvg name="bolt" type="common" width={14} height={14} />
                      </span>
                      {/* Clipped, not deleted: the count is what screen readers announce and
                        what the E2E credit assertions read. On screen the bolt's tint carries
                        the state and the tooltip carries the number. */}
                      <span className="mcp-chat__balance-pill-text">
                        {this.props.remainingBalance === null
                          ? "Unlimited"
                          : Math.floor(this.props.remainingBalance).toLocaleString("en-US")}
                      </span>
                    </button>
                  </KuikaTooltip>
                )}
                {this.props.onProjectAnalysis && (
                  <KuikaTooltip
                    content="Analyze project — summarize every screen so the AI understands it. Shows the cost first."
                    position="top"
                  >
                    <button
                      className="mcp-chat__toolbar-btn mcp-chat__toolbar-btn--analyze"
                      onClick={this.props.onProjectAnalysis}
                      disabled={isLoading}
                      type="button"
                      aria-label="Analyze project"
                      data-testid="mcpChatProjectAnalysis"
                    >
                      <KuikaSvg name="scan-eye" type="common" width={16} height={16} />
                    </button>
                  </KuikaTooltip>
                )}
                <KuikaTooltip content="Voice input" position="top">
                  <button
                    className="mcp-chat__toolbar-btn mcp-chat__toolbar-btn--voice"
                    onClick={this.handleMicClick}
                    disabled={isLoading}
                    aria-label="Voice input"
                  >
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 1a2.5 2.5 0 0 0-2.5 2.5v4a2.5 2.5 0 0 0 5 0v-4A2.5 2.5 0 0 0 8 1z" />
                      <path d="M3.5 7.5a.5.5 0 0 1 1 0 3.5 3.5 0 0 0 7 0 .5.5 0 0 1 1 0 4.5 4.5 0 0 1-4 4.473V13.5h2a.5.5 0 0 1 0 1h-5a.5.5 0 0 1 0-1h2v-1.527a4.5 4.5 0 0 1-4-4.473z" />
                    </svg>
                  </button>
                </KuikaTooltip>
                {isLoading ? (
                  <button
                    className="mcp-chat__toolbar-stop"
                    data-testid="mcpChatStopButton"
                    onClick={onStop}
                    title="Stop generating"
                    aria-label="Stop generating"
                  >
                    <svg width="14" height="14" viewBox="0 0 10 10" fill="currentColor">
                      <rect width="10" height="10" rx="1.5" />
                    </svg>
                  </button>
                ) : (
                  <KuikaButton
                    type="primary"
                    isAIButton
                    isIcon
                    size="sm"
                    disabled={!this.state.hasContent}
                    onClick={onSend}
                    className="mcp-chat__toolbar-send"
                    aria-label="Send message"
                  >
                    <KuikaSvg name="arrow-up" type="arrows" width={18} height={18} />
                  </KuikaButton>
                )}
              </div>
            </div>
          )}
        </div>
        {errorMessage && (
          <div className="mcp-chat__recording-error">
            <span>{errorMessage}</span>
            <button onClick={this.dismissError} aria-label="Dismiss error">
              ×
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default McpChatInputArea;
